<template>
  <div class="flex flex-col items-center py-8 px-4">
    <div class="w-full max-w-lg">
      <div class="mb-6">
        <h2 class="text-base font-semibold text-gray-800">Business Name Effect</h2>
        <p class="text-sm text-gray-500 mt-1">
          Enter a tracking number to manually effect a business name filing task.
        </p>
      </div>

      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <form class="space-y-4" @submit.prevent="onSubmit">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Tracking number</label>
            <input
              v-model.trim="trackingNo"
              type="text"
              placeholder="e.g. G260717-4531"
              required
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              :disabled="loading"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Approval action
            </label>
            <select
              v-model="approvalAction"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              :disabled="loading"
            >
              <option value="">Select</option>
              <option value="TO_SOLE">TO_SOLE</option>
              <option value="CEASE">CEASE</option>
            </select>
          </div>

          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="loading || !trackingNo">
              <i
                class="fa mr-1.5"
                :class="loading ? 'fa-spinner fa-spin' : 'fa-check'"
              ></i>
              {{ loading ? "Submitting..." : "Effect filing" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { api } from "@/app/services/api.service";
import { useConfirmDialog, useToast } from "@/composables";

const { successToast, errorToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();

type ApprovalActionValue = "TO_SOLE" | "CEASE";
type ApprovalAction = "" | ApprovalActionValue;

const loading = ref(false);
const trackingNo = ref("");
const approvalAction = ref<ApprovalAction>("");

const extractErrorMessage = (error: unknown, fallback: string) => {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err.response?.data?.message || err.message || fallback;
};

const onSubmit = async () => {
  const tracking = trackingNo.value.trim();
  if (!tracking) {
    warningToast("Validation", "Enter a tracking number");
    return;
  }

  const selectedAction = approvalAction.value || null;
  const confirmMessage = selectedAction
    ? `Effect filing for tracking number ${tracking} with approval action ${selectedAction}? This action is destructive and cannot be easily undone.`
    : `Effect filing for tracking number ${tracking}?`;

  const confirmed = await showConfirm(confirmMessage, {
    title: "Confirm Business Name Effect",
    variant: selectedAction ? "danger" : "primary",
    okTitle: "Effect filing",
    cancelTitle: "Cancel",
  });
  if (!confirmed) return;

  loading.value = true;
  try {
    const payload: {
      trackingNo: string;
      approvalAction: ApprovalActionValue | null;
    } = {
      trackingNo: tracking,
      approvalAction: selectedAction,
    };

    const response = await api().post(
      "/business-registration/filing/task/manual-effect",
      payload
    );
    const message =
      (response?.data as { message?: string } | undefined)?.message ||
      "Business name effect submitted";
    successToast("Success", message);
    trackingNo.value = "";
    approvalAction.value = "";
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not effect business name filing"));
  } finally {
    loading.value = false;
  }
};
</script>
